<template>
  <div
    class="lease-blocked-modal"
    :class="$theme.darkTheme ? 'lease-blocked-modal--dark' : 'lease-blocked-modal--light'"
  >
    <div class="modal-icon">
      <i class="material-icons" :style="{ color: accent }">{{ iconName }}</i>
    </div>
    <h5>{{ title }}</h5>
    <p>{{ body }}</p>
    <button
      v-if="showReconnect && isConnected"
      class="btn-reconnect"
      :style="{ background: accent, color: accentText }"
      :disabled="isLoading"
      @click="handleReconnect"
    >{{ isLoading ? 'Reconectando...' : 'Reconectar agora' }}</button>
    <p v-else-if="showReconnect" class="offline-hint">
      <i class="material-icons">wifi_off</i>
      <span>Você está offline</span>
    </p>
    <button
      class="btn-close"
      :style="{ color: accent, borderColor: accent }"
      :disabled="isLoading"
      @click="onClose"
    >Fechar</button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'LeaseBlockedModal',
  props: {
    // 'expired' — TTL+grace passaram. Banner: "Reconecte para continuar"
    // 'first-revalidate' — download pré-feature sem lease. Banner: "Conecte-se uma vez para liberar"
    // 'revoked' — backend bateu 402. Banner: "Acesso suspenso"
    mode: { type: String, default: 'expired' },
    enrollId: { type: [String, Number], default: null },
    onClose: { type: Function, required: true }
  },
  data () {
    return { isLoading: false };
  },
  computed: {
    ...mapGetters({ isConnected: 'dom/isConnected' }),
    iconName () {
      return this.mode === 'revoked' ? 'block' : 'cloud_off';
    },
    title () {
      if (this.mode === 'revoked') return 'Acesso suspenso';
      if (this.mode === 'first-revalidate') return 'Conecte-se uma vez';
      return 'Reconecte para continuar';
    },
    body () {
      if (this.mode === 'revoked') {
        return 'Sua assinatura foi suspensa pelo backend. Regularize sua assinatura para voltar a acessar este curso.';
      }
      if (this.mode === 'first-revalidate') {
        return 'Estes downloads foram feitos antes da última atualização. Conecte-se à internet uma vez para liberá-los offline.';
      }
      return 'Para manter o acesso aos seus downloads, conecte-se à internet. Sua matrícula precisa ser revalidada.';
    },
    showReconnect () {
      // No modo 'revoked' o caminho não é reconectar — é regularizar a
      // assinatura (que provavelmente acontece em outra tela). Esconde o
      // botão pra não confundir.
      return this.mode !== 'revoked';
    },
    accent () {
      // Whitelabel: cor primária do produtor (igual aos demais modais).
      return this.$theme.primaryColor || '#0564b1';
    },
    accentText () {
      return this.$theme.primaryText || '#fff';
    }
  },
  methods: {
    async handleReconnect () {
      if (!this.enrollId) {
        this.onClose();
        return;
      }
      this.isLoading = true;
      try {
        const ok = await this.$store.dispatch('lease/forceRevalidate', { enrollId: this.enrollId });
        if (ok) this.onClose();
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.lease-blocked-modal {
  padding: 32px 28px;
  text-align: center;
  border-radius: 20px;

  &--light {
    background: #ffffff;
    h5 { color: #1a1a2e; }
    p  { color: #717171; }
  }

  &--dark {
    background: #16161f;
    h5 { color: #f0f0f0; }
    p  { color: #6e6e82; }
  }

  .modal-icon {
    margin-bottom: 12px;

    i {
      font-size: 42px;
    }
  }

  h5 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px;
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 24px;
  }

  // Estado (não ação): sinaliza offline sem parecer um botão clicável.
  .offline-hint {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 500;
    color: #8b8b9e;

    i { font-size: 18px; }
  }

  .btn-reconnect,
  .btn-close {
    display: block;
    width: 100%;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;

    & + & {
      margin-top: 10px;
    }
  }

  .btn-reconnect {
    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-close {
    background: transparent;
    border: 1px solid;

    &:hover:not(:disabled) {
      opacity: 0.85;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
</style>
